// Kajeet UI Kit primitives, ported from the kit's Tailwind markup to the kj-* classes (the kit's own
// components.css plus kit.css). Same names and props as the kit, so the kit's docs apply.
import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type CSSProperties,
  cloneElement,
  createContext,
  type ElementType,
  type FormEvent,
  type HTMLAttributes,
  type InputHTMLAttributes,
  isValidElement,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Link } from "react-router";
import { cn } from "./cn";
import { Icon, type IconName } from "./Icon";

// ------------------------------------------------------------------ buttons
export type ButtonVariant = "primary" | "secondary" | "ghost" | "spark" | "danger" | "chrome";
export type ButtonSize = "sm" | "md" | "lg";

type ButtonCommon = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconRight?: IconName;
  /** Shows a spinner and disables the button. Keep the label ("Saving…"). */
  loading?: boolean;
  children?: ReactNode;
};

const ICON_SIZE: Record<ButtonSize, number> = { sm: 14, md: 16, lg: 18 };

export function buttonClasses({ variant = "primary", size = "md", className }: { variant?: ButtonVariant; size?: ButtonSize; className?: string }) {
  return cn("kj-btn", `kj-btn-${variant}`, size === "sm" && "kj-btn-sm", size === "lg" && "kj-btn-lg", className);
}

function ButtonContent({ icon, iconRight, loading, size = "md", children }: ButtonCommon) {
  return (
    <>
      {loading ? (
        <span aria-hidden className="kj-spinner" style={{ width: ICON_SIZE[size], height: ICON_SIZE[size], color: "currentColor" }} />
      ) : icon ? (
        <Icon name={icon} size={ICON_SIZE[size]} />
      ) : null}
      {children}
      {iconRight ? <Icon name={iconRight} size={ICON_SIZE[size]} /> : null}
    </>
  );
}

/** One `primary` (action blue) per view; `spark` (lime) at most once per screen; `chrome` on the emerald bar. */
export function Button({
  variant,
  size,
  icon,
  iconRight,
  loading,
  className,
  children,
  disabled,
  type = "button",
  ...rest
}: ButtonCommon & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type={type} disabled={disabled || loading} aria-busy={loading || undefined} className={buttonClasses({ variant, size, className })} {...rest}>
      <ButtonContent icon={icon} iconRight={iconRight} loading={loading} size={size}>
        {children}
      </ButtonContent>
    </button>
  );
}

/** A link styled as a button: a router link for app routes, a plain anchor for `external` or server routes (`reload`). */
export function ButtonLink({
  variant,
  size,
  icon,
  iconRight,
  className,
  children,
  href,
  external,
  reload,
  ...rest
}: Omit<ButtonCommon, "loading"> &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
    href: string;
    /** Opens in a new tab with an external-link icon. */
    external?: boolean;
    /** A full page load (sign-in, sign-out, downloads) instead of a client-side route change. */
    reload?: boolean;
  }) {
  const classes = buttonClasses({ variant, size, className });
  const content = (
    <ButtonContent icon={icon} iconRight={iconRight ?? (external ? "external-link" : undefined)} size={size}>
      {children}
    </ButtonContent>
  );
  if (external) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={classes} {...rest}>
        {content}
      </a>
    );
  }
  if (reload) {
    return (
      <a href={href} className={classes} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <Link to={href} className={classes} {...rest}>
      {content}
    </Link>
  );
}

/** Icon-only button. `label` is required: it becomes the accessible name and the tooltip. */
export function IconButton({
  icon,
  label,
  variant = "ghost",
  size = "md",
  className,
  type = "button",
  ...rest
}: {
  icon: IconName;
  label: string;
  variant?: ButtonVariant;
  size?: "sm" | "md";
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">) {
  return (
    <button type={type} aria-label={label} title={label} className={cn("kj-btn kj-btn-icon", `kj-btn-${variant}`, size === "sm" && "kj-btn-sm", className)} {...rest}>
      <Icon name={icon} size={size === "sm" ? 15 : 18} />
    </button>
  );
}

// ------------------------------------------------------------------ cards
const PADDING = { none: "", sm: "kj-card-pad-sm", md: "kj-card-pad-md", lg: "kj-card-pad-lg" } as const;

/** Surface card: white (charcoal in dark), 14px radius, soft shadow. `padding="none"` for a table or list. */
export function Card({
  as: As = "div",
  padding = "md",
  hoverable,
  className,
  children,
  ...rest
}: {
  as?: ElementType;
  padding?: keyof typeof PADDING;
  hoverable?: boolean;
  className?: string;
  children?: ReactNode;
} & HTMLAttributes<HTMLElement>) {
  return (
    <As className={cn("kj-card", hoverable && "kj-card-hoverable", PADDING[padding], padding === "none" && "kj-card-clip", className)} {...rest}>
      {children}
    </As>
  );
}

/** Filter and action bar across the top of a card that holds a table or list. */
export function Toolbar({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("kj-toolbar", className)}>{children}</div>;
}

/** Card heading row: kicker, title, optional description and actions. `flush` inside a card with no padding. */
export function CardHeader({
  kicker,
  title,
  description,
  actions,
  className,
  flush,
  as: Heading = "h2",
}: {
  kicker?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  flush?: boolean;
  as?: "h2" | "h3" | "h4";
}) {
  return (
    <div className={cn("kj-card-header", flush && "flush", className)}>
      <div>
        {kicker ? <p className="kj-kicker">{kicker}</p> : null}
        <Heading className="kj-card-title">{title}</Heading>
        {description ? <p className="kj-card-desc">{description}</p> : null}
      </div>
      {actions ? <div className="kj-card-actions">{actions}</div> : null}
    </div>
  );
}

// ------------------------------------------------------------------ status
export type Tone = "ok" | "warn" | "crit" | "info" | "muted" | "brand";

/** Status pill: a word on a tint in the matching ink. Never colour alone. */
export function Pill({ tone = "muted", dot, className, title, children }: { tone?: Tone; dot?: boolean; className?: string; title?: string; children: ReactNode }) {
  return (
    <span className={cn(`kj-pill-${tone}`, className)} title={title}>
      {dot ? <span aria-hidden className="kj-pill-dot" /> : null}
      {children}
    </span>
  );
}

/** Status dot with a soft halo. Pair it with a text label. */
export function Dot({ tone = "ok", className }: { tone?: "ok" | "warn" | "crit" | "muted"; className?: string }) {
  return <span aria-hidden className={cn(`kj-dot-${tone}`, className)} />;
}

/** The kit's shared vocabulary of status words. Apps add their own through `map`. */
export const STATUS_TONES: Record<string, Tone> = {
  active: "ok",
  online: "ok",
  healthy: "ok",
  connected: "ok",
  complete: "ok",
  completed: "ok",
  success: "ok",
  approved: "ok",
  resolved: "ok",
  pending: "warn",
  degraded: "warn",
  warning: "warn",
  "in review": "warn",
  "on hold": "warn",
  failed: "crit",
  error: "crit",
  offline: "crit",
  suspended: "crit",
  critical: "crit",
  overdue: "crit",
  rejected: "crit",
  cancelled: "crit",
  new: "info",
  processing: "info",
  scheduled: "info",
  open: "info",
  draft: "muted",
  inactive: "muted",
  archived: "muted",
  unknown: "muted",
};

export function StatusPill({ status, map, dot = true, className }: { status: string; map?: Record<string, Tone>; dot?: boolean; className?: string }) {
  const key = status.trim().toLowerCase();
  const tone = map?.[key] ?? STATUS_TONES[key] ?? "muted";
  return (
    <Pill tone={tone} dot={dot} className={className}>
      {status}
    </Pill>
  );
}

// ------------------------------------------------------------------ forms
export function Input({
  className,
  leadingIcon,
  wrapperClassName,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { leadingIcon?: IconName; wrapperClassName?: string }) {
  if (!leadingIcon) return <input className={cn("kj-input", className)} {...rest} />;
  return (
    <span className={cn("kj-input-icon", wrapperClassName)}>
      <Icon name={leadingIcon} size={16} />
      <input className={cn("kj-input", className)} {...rest} />
    </span>
  );
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cn("kj-select", className)} {...rest}>
      {children}
    </select>
  );
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn("kj-textarea", className)} {...rest} />;
}

type ControlProps = { id?: string; "aria-describedby"?: string; "aria-invalid"?: boolean; required?: boolean };

/** Label, control, hint and error, wired for assistive tech. Takes one control as its child. */
export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactElement<ControlProps>;
}) {
  const autoId = useId();
  const id = (isValidElement(children) && children.props.id) || autoId;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={cn("kj-field", className)}>
      <label htmlFor={id} className="kj-label">
        {label}
        {required ? (
          <span className="kj-required" aria-hidden>
            *
          </span>
        ) : null}
      </label>
      {cloneElement(children, {
        id,
        required: required ?? children.props.required,
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : undefined,
      })}
      {hint ? (
        <p id={hintId} className="kj-field-hint">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="kj-field-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ feedback
/** Uppercase tracked label above headings and metrics. */
export function Kicker({ tone = "subtle", className, children }: { tone?: "subtle" | "teal"; className?: string; children: ReactNode }) {
  return <p className={cn("kj-kicker", tone === "teal" && "teal", className)}>{children}</p>;
}

/** The one Kajeet spinner. */
export function Spinner({ size = 20, label, className }: { size?: number; label?: string; className?: string }) {
  return (
    <span role={label ? "status" : undefined} className={cn("kj-spinner-row", className)}>
      <span aria-hidden className="kj-spinner" style={{ width: size, height: size }} />
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  );
}

/** Loading placeholder block. Shape it like the content it stands in for. */
export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return <div aria-hidden className={cn("kj-skeleton", className)} style={style} />;
}

const BANNER_ICON: Record<"info" | "ok" | "warn" | "crit", IconName> = { info: "info", ok: "check-circle", warn: "alert", crit: "x-circle" };

/** Inline notice above the content it refers to. `crit` and `warn` are announced as alerts. */
export function Banner({
  tone = "info",
  title,
  action,
  onDismiss,
  className,
  children,
}: {
  tone?: "info" | "ok" | "warn" | "crit";
  title?: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
  className?: string;
  children?: ReactNode;
}) {
  const urgent = tone === "crit" || tone === "warn";
  return (
    <div role={urgent ? "alert" : "status"} className={cn("kj-banner", `kj-banner-${tone}`, className)}>
      <Icon name={BANNER_ICON[tone]} size={18} />
      <div className="kj-banner-body">
        {title ? <p className="kj-banner-title">{title}</p> : null}
        {children ? <div className="kj-banner-text">{children}</div> : null}
      </div>
      {action ? <div className="kj-banner-action">{action}</div> : null}
      {onDismiss ? (
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="kj-banner-dismiss">
          <Icon name="x" size={14} />
        </button>
      ) : null}
    </div>
  );
}

/** Round initial avatar. Teal for Kajeet people, `soft` for customers. */
export function Avatar({ name, size = 32, tone = "teal", className }: { name: string; size?: number; tone?: "teal" | "chrome" | "soft"; className?: string }) {
  const initials = name
    .replace(/<.*>/, "")
    .replace(/@.*/, "")
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <span
      aria-hidden
      className={cn("kj-avatar", tone !== "teal" && tone, className)}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.38)) }}
    >
      {initials || "?"}
    </span>
  );
}

/** KPI tile. `variant="card"` stands alone in a grid; `variant="inset"` sits inside a card. `href` makes it a link. */
export function StatTile({
  label,
  value,
  sub,
  delta,
  deltaTone = "muted",
  valueTone,
  icon,
  variant = "card",
  href,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  delta?: ReactNode;
  deltaTone?: "ok" | "warn" | "crit" | "muted";
  /** Colours the number itself for a threshold that is crossed (semantic data only). */
  valueTone?: "ok" | "warn" | "crit";
  icon?: IconName;
  variant?: "card" | "inset";
  href?: string;
  className?: string;
}) {
  const classes = cn("kj-stat", variant === "card" ? "kj-card" : "inset", href && variant === "card" && "kj-card-hoverable", className);
  const body = (
    <>
      <div className="kj-stat-top">
        <p className="kj-stat-label">{label}</p>
        {icon ? (
          <span className="kj-stat-icon">
            <Icon name={icon} size={16} />
          </span>
        ) : null}
      </div>
      <p className={cn("kj-stat-value", valueTone && `kj-tone-${valueTone}`)}>{value}</p>
      {delta || sub ? (
        <p className="kj-stat-sub">
          {delta ? <span className={cn("kj-stat-delta", `kj-tone-${deltaTone}`)}>{delta}</span> : null}
          {sub ? <span>{sub}</span> : null}
        </p>
      ) : null}
    </>
  );
  return href ? (
    <Link to={href} className={classes}>
      {body}
    </Link>
  ) : (
    <div className={classes}>{body}</div>
  );
}

export type StateKind = "empty" | "signed-out" | "unavailable" | "error" | "info";

const STATE_ICON: Record<StateKind, IconName> = { empty: "package", "signed-out": "lock", unavailable: "alert", error: "x-circle", info: "info" };

/**
 * Page or section state: nothing here yet, sign-in required, unavailable, failed to load.
 * Empty copy: title "No <things> yet", one sentence on how one gets created, one action.
 * `layout="panel"` is a centred card for a whole page; `layout="inline"` is the dashed box inside a list or card.
 */
export function StatePanel({
  kind = "empty",
  icon,
  title,
  children,
  action,
  layout = "panel",
  compact,
  className,
  headingLevel = "h2",
}: {
  kind?: StateKind;
  icon?: IconName;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  layout?: "panel" | "inline";
  /** Inline only: less padding, for a state inside a small card. */
  compact?: boolean;
  className?: string;
  headingLevel?: "h1" | "h2" | "h3";
}) {
  const Heading = headingLevel;
  return (
    <section role={kind === "error" ? "alert" : undefined} className={cn("kj-state", layout, compact && "compact", className)}>
      <span className={cn("kj-state-icon", kind === "error" && "error", kind === "unavailable" && "unavailable")}>
        <Icon name={icon ?? STATE_ICON[kind]} size={20} />
      </span>
      <Heading className="kj-state-title">{title}</Heading>
      {children ? <div className="kj-state-body">{children}</div> : null}
      {action ? <div className="kj-state-action">{action}</div> : null}
    </section>
  );
}

// ------------------------------------------------------------------ dialog
/**
 * Modal on the native <dialog>: focus is trapped, the page behind is inert, Escape closes and focus
 * returns to the opener. Pass `onSubmit` and the body and footer become one form, so a `type="submit"`
 * button in `footer` submits and Enter works. Pass `dismissible={false}` while saving.
 * The body renders only while the dialog is open, so it always opens clean.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  onSubmit,
  dismissible = true,
  size = "md",
  placement = "center",
  className,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  dismissible?: boolean;
  size?: "sm" | "md" | "lg";
  placement?: "center" | "right";
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      opener.current = document.activeElement;
      dialog.showModal();
      const prior = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prior;
      };
    }
    if (!open && dialog.open) {
      dialog.close();
      if (opener.current instanceof HTMLElement) opener.current.focus();
    }
  }, [open]);

  const requestClose = () => {
    if (dismissible) onClose();
  };

  const content = open ? (
    <>
      <div className="kj-dialog-body">{children}</div>
      {footer ? <footer className="kj-dialog-footer">{footer}</footer> : null}
    </>
  ) : null;

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onCancel={(event) => {
        event.preventDefault();
        requestClose();
      }}
      onClose={() => {
        if (open) onClose();
      }}
      onClick={(event) => {
        // A click on the backdrop lands on the dialog element itself.
        if (event.target === ref.current) requestClose();
      }}
      className={cn("kj-dialog", placement === "center" ? cn("kj-dialog-center", `kj-dialog-${size}`) : "kj-dialog-right", className)}
    >
      <header className="kj-dialog-header">
        <div>
          <h2 id={titleId}>{title}</h2>
          {description ? (
            <p id={descId} className="kj-dialog-desc">
              {description}
            </p>
          ) : null}
        </div>
        <button type="button" onClick={requestClose} disabled={!dismissible} aria-label="Close" className="kj-dialog-close">
          <Icon name="x" size={18} />
        </button>
      </header>
      {onSubmit ? (
        <form
          className="kj-dialog-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit(event);
          }}
        >
          {content}
        </form>
      ) : (
        content
      )}
    </dialog>
  );
}

// ------------------------------------------------------------------ toast
type ToastTone = "neutral" | "ok" | "crit";
type ToastState = { id: number; message: string; tone: ToastTone } | null;

const ToastContext = createContext<(message: string, options?: { tone?: ToastTone; duration?: number }) => void>(() => {});

/** Transient confirmation ("Link copied", "Settings saved"): past tense, four words at most. Errors that need action use a Banner. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const notify = useCallback((message: string, options?: { tone?: ToastTone; duration?: number }) => {
    clearTimeout(timer.current);
    setToast({ id: Date.now(), message, tone: options?.tone ?? "neutral" });
    timer.current = setTimeout(() => setToast(null), options?.duration ?? 3200);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  const icon = toast?.tone === "ok" ? "check-circle" : toast?.tone === "crit" ? "alert" : null;
  return (
    <ToastContext.Provider value={notify}>
      {children}
      <div role="status" aria-live="polite" className={cn("kj-toast kj-on-chrome", toast && "on")}>
        {icon ? <Icon name={icon} size={14} className={toast?.tone === "ok" ? "ok" : "crit"} /> : null}
        {toast?.message}
      </div>
    </ToastContext.Provider>
  );
}

/** `const notify = useToast(); notify("Link copied", { tone: "ok" })` */
export function useToast() {
  return useContext(ToastContext);
}

// ------------------------------------------------------------------ menu
const MenuContext = createContext<{ close: () => void }>({ close: () => {} });

type TriggerProps = { onClick?: () => void; "aria-haspopup"?: "menu"; "aria-expanded"?: boolean; "aria-controls"?: string; id?: string };

/** Menu button and popover. Closes on outside click, Escape (focus returns to the trigger) and selection; arrow keys move. */
export function DropdownMenu({
  trigger,
  children,
  align = "end",
  width = 240,
  className,
}: {
  trigger: ReactElement<TriggerProps>;
  children: ReactNode;
  align?: "start" | "end";
  width?: number;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const triggerId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    menu.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus();
    return () => document.removeEventListener("pointerdown", onPointer);
  }, [open]);

  const close = () => {
    setOpen(false);
    document.getElementById(triggerId)?.focus();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      close();
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const items = [...(menu.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? [])];
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
    items[next]?.focus();
  };

  return (
    <div ref={root} className={cn("kj-menu-root", className)} onKeyDown={open ? onKeyDown : undefined}>
      {cloneElement(trigger, {
        id: triggerId,
        onClick: () => setOpen((value) => !value),
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": open ? menuId : undefined,
      })}
      {open ? (
        <MenuContext.Provider value={{ close }}>
          <div ref={menu} id={menuId} role="menu" aria-labelledby={triggerId} className={cn("kj-menu", align === "start" && "start")} style={{ width }}>
            {children}
          </div>
        </MenuContext.Provider>
      ) : null}
    </div>
  );
}

export function MenuItem({
  icon,
  href,
  reload,
  onSelect,
  tone = "default",
  children,
}: {
  icon?: IconName;
  href?: string;
  /** With `href`: a full page load (sign-out) instead of a client-side route change. */
  reload?: boolean;
  onSelect?: () => void;
  tone?: "default" | "muted" | "crit";
  children: ReactNode;
}) {
  const { close } = useContext(MenuContext);
  const classes = cn("kj-menu-item", tone !== "default" && tone);
  const content = (
    <>
      {icon ? <Icon name={icon} size={15} /> : null}
      <span>{children}</span>
    </>
  );
  if (href && reload) {
    return (
      <a href={href} role="menuitem" tabIndex={-1} className={classes}>
        {content}
      </a>
    );
  }
  if (href) {
    return (
      <Link to={href} role="menuitem" tabIndex={-1} className={classes} onClick={() => close()}>
        {content}
      </Link>
    );
  }
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      className={classes}
      onClick={() => {
        onSelect?.();
        close();
      }}
    >
      {content}
    </button>
  );
}

export function MenuSeparator() {
  return <div role="separator" className="kj-menu-sep" />;
}

/** Non-interactive header block inside a menu (the signed-in user). */
export function MenuHeader({ children }: { children: ReactNode }) {
  return <div className="kj-menu-header">{children}</div>;
}

// ------------------------------------------------------------------ tabs
export type TabItem<T extends string = string> = { id: T; label: ReactNode; icon?: IconName; badge?: ReactNode };

/**
 * Line tabs for the sections of a page, with a roving tabindex (arrow keys, Home, End). Controlled:
 * the page keeps the active tab (usually in the URL) and renders the panel under `panelId(active)`.
 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  idBase,
  className,
}: {
  tabs: TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  /** Accessible name for the tab list. */
  label: string;
  /** Prefix for tab and panel ids: tab `${idBase}-tab-${id}`, panel `${idBase}-panel-${id}`. */
  idBase: string;
  className?: string;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tabs.length - 1;
    const next =
      event.key === "ArrowRight" ? (index === last ? 0 : index + 1) : event.key === "ArrowLeft" ? (index === 0 ? last : index - 1) : event.key === "Home" ? 0 : event.key === "End" ? last : null;
    if (next === null) return;
    event.preventDefault();
    const tab = tabs[next];
    if (tab) onChange(tab.id);
    listRef.current?.querySelectorAll<HTMLButtonElement>("[role=tab]")[next]?.focus();
  };
  return (
    <div ref={listRef} role="tablist" aria-label={label} className={cn("kj-tabs", className)}>
      {tabs.map((tab, index) => {
        const selected = tab.id === value;
        return (
          <button
            key={tab.id}
            id={`${idBase}-tab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`${idBase}-panel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className="kj-tab"
          >
            {tab.icon ? <Icon name={tab.icon} size={15} /> : null}
            {tab.label}
            {tab.badge != null ? <span className="kj-tab-badge">{tab.badge}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/** The panel for the active tab of a `Tabs` with the same `idBase`. */
export function TabPanel({ idBase, id, className, children }: { idBase: string; id: string; className?: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={`${idBase}-panel-${id}`} aria-labelledby={`${idBase}-tab-${id}`} className={className}>
      {children}
    </div>
  );
}

/** Small exclusive toggle ("Day / Week / Month"), with radio-group semantics. */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: { value: T; label: ReactNode }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  const group = useRef<HTMLDivElement>(null);
  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = options.length - 1;
    const next =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? index === last
          ? 0
          : index + 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? index === 0
            ? last
            : index - 1
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    const option = options[next];
    if (option) onChange(option.value);
    group.current?.querySelectorAll<HTMLButtonElement>("[role=radio]")[next]?.focus();
  };
  return (
    <div ref={group} role="radiogroup" aria-label={label} className={cn("kj-segmented", className)}>
      {options.map((option, index) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => move(event, index)}
            className="kj-segment"
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------ page
/** Top of a page on the content sheet: the page's one H1, a one-sentence description, actions (primary last). */
export function PageHeader({
  title,
  description,
  actions,
  divider = true,
  record,
  back,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  divider?: boolean;
  /** A record page (one case, one incident): a smaller title, so a long subject still reads as a heading. */
  record?: boolean;
  /** A "Back to …" link above the title. */
  back?: { to: string; label: string };
  className?: string;
}) {
  return (
    <header className={cn("kj-page-header", divider && "divider", record && "kj-record", className)}>
      <div className="kj-page-header-text">
        {back ? (
          <p style={{ marginBottom: 12 }}>
            <Link to={back.to} className="kj-back">
              <Icon name="arrow-left" size={14} />
              {back.label}
            </Link>
          </p>
        ) : null}
        <h1>{title}</h1>
        {description ? <p className="kj-page-header-desc">{description}</p> : null}
      </div>
      {actions ? <div className="kj-page-header-actions">{actions}</div> : null}
    </header>
  );
}
